import React from "react";
import { Checkbox } from "../../../../ui/components/Checkbox";
import { Badge } from "../../../../ui/components/Badge";

interface PermissionCardsProps {
  rolePermissions: Record<
    string,
    {
      module_name: string;
      actions: Record<
        string,
        {
          action_name: string;
          is_granted: boolean;
          requires_review: boolean;
        }
      >;
    }
  >;
  userPermissions?: Record<string, Record<string, boolean>>; // module_code -> action_code -> is_granted
  onChange?: (permissions: Record<string, Record<string, boolean>>) => void;
  readOnly?: boolean;
}

export const PermissionCards: React.FC<PermissionCardsProps> = ({
  rolePermissions,
  userPermissions = {},
  onChange,
  readOnly = false,
}) => {
  const handlePermissionChange = (
    moduleCode: string,
    actionCode: string,
    granted: boolean
  ) => {
    if (readOnly || !onChange) return;

    const newPermissions = {
      ...userPermissions,
      [moduleCode]: {
        ...userPermissions[moduleCode],
        [actionCode]: granted,
      },
    };
    onChange(newPermissions);
  };

  const handleModuleSelectAll = (moduleCode: string, grant: boolean) => {
    if (readOnly || !onChange) return;

    const module = rolePermissions[moduleCode];
    if (!module) return;

    const newPermissions: Record<string, Record<string, boolean>> = {
      ...userPermissions,
      [moduleCode]: {},
    };

    // Set all actions in module to grant or remove them
    Object.keys(module.actions).forEach((actionCode) => {
      if (grant) {
        // Only grant if role has permission
        if (module.actions[actionCode].is_granted) {
          newPermissions[moduleCode][actionCode] = true;
        }
      } else {
        // Deny all (remove from overrides or set to false)
        newPermissions[moduleCode][actionCode] = false;
      }
    });

    // Remove module key if empty
    if (Object.keys(newPermissions[moduleCode]).length === 0) {
      delete newPermissions[moduleCode];
    }

    onChange(newPermissions);
  };

  const getEffectivePermission = (
    moduleCode: string,
    actionCode: string
  ): boolean => {
    // Check user override first
    if (
      moduleCode in userPermissions &&
      actionCode in userPermissions[moduleCode]
    ) {
      return userPermissions[moduleCode][actionCode];
    }
    // Fall back to role permission
    return (
      rolePermissions[moduleCode]?.actions[actionCode]?.is_granted ?? false
    );
  };

  const getRequiresReview = (
    moduleCode: string,
    actionCode: string
  ): boolean => {
    return (
      rolePermissions[moduleCode]?.actions[actionCode]?.requires_review ?? false
    );
  };

  // Get display text for action (add "(Upload)" for Create in specific modules)
  const getActionDisplayText = (
    actionName: string,
    actionCode: string,
    moduleCode: string
  ): string => {
    if (
      actionCode === "create" &&
      (moduleCode === "bill" ||
        moduleCode === "invoice" ||
        moduleCode === "bank_transaction")
    ) {
      return `${actionName} (Upload)`;
    }
    return actionName;
  };

  // Filter modules: in read-only mode, show all modules; otherwise only show modules with granted actions
  const modulesWithPermissions = readOnly
    ? Object.entries(rolePermissions)
    : Object.entries(rolePermissions).filter(([moduleCode, module]) => {
        return Object.values(module.actions).some(
          (action) => action.is_granted
        );
      });

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {modulesWithPermissions.map(([moduleCode, module]) => (
        <div
          key={moduleCode}
          className="flex flex-col gap-3 border border-solid border-neutral-border rounded-lg p-4 bg-default-background"
        >
          {/* Module Header */}
          <div className="flex items-center justify-between border-b border-solid border-neutral-border pb-2">
            <div className="flex items-center gap-2">
              <span className="text-heading-3 font-heading-3 text-default-font">
                {module.module_name}
              </span>
            </div>
            {!readOnly && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleModuleSelectAll(moduleCode, true)}
                  className="text-sm text-brand-600 hover:text-brand-700 px-2 py-1 rounded hover:bg-brand-50"
                >
                  Grant All
                </button>
                <span className="text-subtext-color">|</span>
                <button
                  type="button"
                  onClick={() => handleModuleSelectAll(moduleCode, false)}
                  className="text-sm text-brand-600 hover:text-brand-700 px-2 py-1 rounded hover:bg-brand-50"
                >
                  Deny All
                </button>
              </div>
            )}
          </div>

          {/* Actions List */}
          <div className="flex flex-col gap-2">
            {Object.entries(module.actions)
              .filter(([_, action]) => (readOnly ? true : action.is_granted)) // In read-only mode, show all actions; otherwise only show granted actions
              .map(([actionCode, action]) => {
                const effectivePermission = getEffectivePermission(
                  moduleCode,
                  actionCode
                );
                const requiresReview = getRequiresReview(
                  moduleCode,
                  actionCode
                );

                return (
                  <div
                    key={actionCode}
                    className="flex items-center justify-between gap-3 p-2 rounded hover:bg-neutral-50"
                  >
                    <div className="flex items-center gap-2 flex-1">
                      <Checkbox
                        checked={effectivePermission}
                        onCheckedChange={(checked) => {
                          if (!readOnly) {
                            handlePermissionChange(
                              moduleCode,
                              actionCode,
                              checked
                            );
                          }
                        }}
                        disabled={readOnly}
                        label={getActionDisplayText(
                          action.action_name,
                          actionCode,
                          moduleCode
                        )}
                      />
                      {requiresReview && (
                        <Badge variant="warning" className="text-xs">
                          (will be required to send for review)
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      ))}
    </div>
  );
};
